import { z } from 'zod';

/**
 * Schema for the GovmapGroup entity of sync table (GovmapGroup)
 * with platform related values
 */
const govmapGroupTableSchema = z.object({
  id: z.string(),
  name: z.string(),
  parent_group_id: z.string().nullable(),
  event_time: z.number().transform((date) => new Date(date)),
  platform_id: z.string(),
  organization_platform_id: z.string(),
  created_at: z.number().transform((date) => new Date(date)),
  updated_at: z.number().transform((date) => new Date(date)),
  deleted_at: z
    .number()
    .nullish()
    .transform((d) => (d ? new Date(d) : null)),
});

type GovmapGroupTableSchemaType = z.infer<typeof govmapGroupTableSchema>;

/**
 * Schema for the GovmapGroup entity of sync table (GovmapGroup)
 * with platform related values
 * transformed
 */

const govmapGroupTransformer = (govmapGroup: GovmapGroupTableSchemaType) => ({
  id: govmapGroup.id,
  name: govmapGroup.name,
  parentGroupId: govmapGroup.parent_group_id,
  organizationPlatformId: govmapGroup.organization_platform_id,
  eventTime: new Date(govmapGroup.event_time),
  platformId: govmapGroup.platform_id,
  createdAt: new Date(govmapGroup.created_at),
  updatedAt: new Date(govmapGroup.updated_at),
  deletedAt: govmapGroup.deleted_at ? new Date(govmapGroup.deleted_at) : null,
});

export const govmapGroupSchema = govmapGroupTableSchema.transform(
  govmapGroupTransformer,
);

export type GovmapGroupSchemaType = z.infer<typeof govmapGroupSchema>;

export const searchGovmapGroupSchema = govmapGroupTableSchema
  .pick({
    id: true,
    parent_group_id: true,
    name: true,
    created_at: true,
    updated_at: true,
  })
  .transform((data) => ({
    id: data.id,
    parentGroupId: data.parent_group_id,
    name: data.name,
    createdAt: new Date(data.created_at),
    updatedAt: new Date(data.updated_at),
  }));

export type SearchGovmapGroupSchemaType = z.infer<
  typeof searchGovmapGroupSchema
>;

export type MinimalGovmapGroup = Pick<
  GovmapGroupSchemaType,
  'id' | 'name' | 'parentGroupId'
>;

export const govmapGroupWithParentNameSchema = govmapGroupTableSchema
  .pick({
    id: true,
    name: true,
    parent_group_id: true,
  })
  .extend({
    parent_group_name: z.string().nullable(),
  })
  .transform((data) => ({
    id: data.id,
    name: data.name,
    parentGroupId: data.parent_group_id,
    parentGroupName: data.parent_group_name,
  }));

export const actionTemplateGovmapGroupsSchema = z
  .object({
    action_id: z.string(),
    groups: z.array(govmapGroupWithParentNameSchema),
  })
  .transform((data) => ({
    actionId: data.action_id,
    groups: data.groups,
  }));

/**
 * collection of govmapGroups
 */
export const govmapGroupsSchema = z.array(govmapGroupWithParentNameSchema);

export type GovmapGroupsSchemaType = z.infer<typeof govmapGroupsSchema>;
export type GovmapGroupWithChildrenType = SearchGovmapGroupSchemaType & {
  children: GovmapGroupWithChildrenType[];
};

/**
 * schema for table IssueGovmapGroups 1-1 raw data
 */
export const issueGovmapGroupsSchema = z.object({
  issue_id: z.string(),
  govmap_group_id: z.string(),
  created_by: z.string().nullable(),
  updated_by: z.string().nullable(),
  created_at: z.number().transform((date) => new Date(date)),
  updated_at: z.number().transform((date) => new Date(date)),
  deleted_at: z
    .number()
    .nullish()
    .transform((d) => (d ? new Date(d) : null)),
});

export type GovmapGroupWithParentNameSchemaType = z.infer<
  typeof govmapGroupWithParentNameSchema
>;
